// Tiroir panier : ouverture, changement de quantite, ajout rapide depuis
// la remontee de produits. Le contenu est re-rendu par le serveur via la
// Section Rendering API, pour ne pas dupliquer le gabarit en JavaScript.

const SECTION = 'mx-panier';
const sobre = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

class MxTiroirPanier extends HTMLElement {
  connectedCallback() {
    this.voile = document.querySelector('[data-voile-panier]');
    this.corps = this.querySelector('[data-corps-panier]');

    this.querySelector('[data-ferme-panier]')?.addEventListener('click', () => this.ferme());
    this.voile?.addEventListener('click', () => this.ferme());

    document.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape' && this.dataset.ouvert === 'true') this.ferme();
    });

    // L'icone de panier de l'en-tete ouvre le tiroir au lieu de naviguer.
    document.querySelectorAll('[data-ouvre-panier]').forEach((el) => {
      el.addEventListener('click', (ev) => { ev.preventDefault(); this.ouvre(); });
    });

    this.brancheActions();

    // Permet a n'importe quel script du theme de demander l'ouverture.
    document.addEventListener('mx:panier:ouvrir', () => this.ouvre());
    document.addEventListener('mx:panier:rafraichir', () => this.rafraichis());

    // Le tiroir est rendu sur chaque page : c'est donc ici qu'un panier
    // herite d'avant le changement de modele se fait nettoyer, sans
    // attendre que le visiteur l'ouvre.
    this.retireLesIntruses();
  }

  ouvre() {
    this.hidden = false;
    if (this.voile) this.voile.hidden = false;
    requestAnimationFrame(() => {
      this.dataset.ouvert = 'true';
      if (this.voile) this.voile.dataset.ouvert = 'true';
    });
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-ferme-panier]')?.focus();
  }

  ferme() {
    this.dataset.ouvert = 'false';
    if (this.voile) this.voile.dataset.ouvert = 'false';
    document.body.style.overflow = '';
    const apres = () => {
      this.hidden = true;
      if (this.voile) this.voile.hidden = true;
    };
    if (sobre()) apres(); else setTimeout(apres, 300);
  }

  brancheActions() {
    this.addEventListener('click', async (ev) => {
      const bouton = ev.target.closest('[data-quantite]');
      if (!bouton) return;
      ev.preventDefault();
      await this.changeQuantite(bouton.dataset.quantite, Number(bouton.dataset.vers));
    });

    this.addEventListener('submit', async (ev) => {
      const forme = ev.target.closest('[data-ajout-rapide]');
      if (!forme) return;
      ev.preventDefault();
      await this.ajoute(new FormData(forme));
    });
  }

  async changeQuantite(cle, quantite) {
    this.dataset.occupe = 'true';
    try {
      const rep = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: cle, quantity: Math.max(0, quantite) }),
      });
      if (!rep.ok) throw new Error(rep.status);
      await this.rafraichis();
    } catch (e) {
      // En cas d'echec, un rechargement de la page du panier reste fiable.
      console.error('Panier : changement de quantité impossible', e);
    } finally {
      this.dataset.occupe = 'false';
    }
  }

  async ajoute(donnees) {
    this.dataset.occupe = 'true';
    try {
      const rep = await fetch('/cart/add.js', { method: 'POST', body: donnees });
      if (!rep.ok) throw new Error(rep.status);
      await this.rafraichis();
    } catch (e) {
      console.error('Panier : ajout impossible', e);
    } finally {
      this.dataset.occupe = 'false';
    }
  }

  /* Re-rend le contenu du tiroir depuis le serveur. */
  async rafraichis() {
    const url = new URL(location.pathname, location.origin);
    url.searchParams.set('sections', SECTION);

    const rep = await fetch(url);
    if (!rep.ok) return;

    const sections = await rep.json();
    const html = sections[SECTION];
    if (!html) return;

    const frais = new DOMParser().parseFromString(html, 'text/html');
    const nouveauCorps = frais.querySelector('[data-corps-panier]');
    if (nouveauCorps && this.corps) this.corps.innerHTML = nouveauCorps.innerHTML;

    const nouveauCompte = frais.querySelector('[data-compte]');
    const compte = this.querySelector('[data-compte]');
    if (nouveauCompte && compte) compte.textContent = nouveauCompte.textContent;

    this.majPastille(Number(nouveauCompte?.textContent.trim() || 0));

    await this.retireLesIntruses();
  }

  /* Retire du panier ce qui n'est ni une box ni un mix.

     Le site ne vend plus de bonbon seul : aucune page n'en propose
     l'achat. Une telle ligne ne peut donc venir que d'une adresse
     /cart/add forgee, ou d'un panier commence avant le changement de
     modele. Le gabarit du tiroir marque ces lignes, parce que lui seul
     voit les etiquettes du produit ; ce script les supprime.

     Honnete sur la portee : ce garde-fou vit dans le navigateur. Il
     couvre tout ce qu'un visiteur peut faire, URL directe comprise,
     mais une requete forgee hors navigateur passerait au travers. Le
     verrou cote serveur demanderait une fonction Shopify, donc une
     application installee sur la boutique. */
  async retireLesIntruses() {
    const intruses = [...this.querySelectorAll('[data-ligne-interdite]')];
    if (!intruses.length) return;

    // Le nettoyage re-rend le tiroir, qui redemande un nettoyage : deux
    // tours suffisent toujours. Si une ligne resiste malgre un retrait
    // accepte par le serveur, ce verrou evite de tourner sans fin.
    if (this.nettoyageEnCours) return;
    this.nettoyageEnCours = true;

    for (const ligne of intruses) {
      const cle = ligne.dataset.ligneCle;
      if (!cle) continue;
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: cle, quantity: 0 }),
        });
      } catch (e) {
        console.error('Panier : ligne non vendable, retrait impossible', e);
        this.nettoyageEnCours = false;
        return;
      }
    }

    try {
      await this.rafraichis();

      // La page du panier affiche les memes lignes que le tiroir, mais
      // le serveur les a rendues avant le nettoyage : elle doit etre
      // relue pour ne pas montrer un total qui n'existe plus.
      if (document.querySelector('[data-page-panier]')) location.reload();
    } finally {
      this.nettoyageEnCours = false;
    }
  }

  majPastille(nombre) {
    document.querySelectorAll('[data-compte-panier]').forEach((el) => {
      el.textContent = String(nombre);
      el.hidden = nombre === 0;
      if (nombre > 0 && !sobre()) {
        el.classList.remove('mx-rebond');
        void el.offsetWidth;
        el.classList.add('mx-rebond');
      }
    });
  }
}

customElements.define('mx-tiroir-panier', MxTiroirPanier);
